import { MutationError } from '../../shared/ui/mutation-error';
import { PanelFoot, SectionPanel } from '../../shared/ui/section-panel';
import { Switch } from '../../shared/ui/switch';
import { Table, type IColumn } from '../../shared/ui/table';
import { Tooltip } from '../../shared/ui/tooltip';
import { ErrorState, LoadingState } from '../../shared/ui/states';
import { cellBlock, cellFor, type TNotificationCategory } from '../../entities/user';
import { useNotificationPreferences } from './use-notification-preferences';

// Same order as the organization table: two matrices on one screen that disagree
// about which column is which is a misread waiting to happen.
const CHANNELS = [
  { key: 'in_app', header: 'In app' },
  { key: 'email', header: 'Email' },
] as const;

interface ICellProps {
  category: TNotificationCategory;
  channel: 'in_app' | 'email';
  onToggle: (category: string, channel: 'in_app' | 'email', enabled: boolean) => void;
  saving: boolean;
}

const Cell = ({ category, channel, onToggle, saving }: ICellProps) => {
  const cell = cellFor(category, channel);
  const blocked = cellBlock(cell);

  const control = (
    <span className="flex w-full">
      <Switch
        size="sm"
        checked={(cell?.availableToOrg && cell.personalEnabled) ?? false}
        disabled={blocked !== null || saving}
        aria-label={`${category.label} - ${channel === 'email' ? 'Email' : 'In app'}`}
        onCheckedChange={(next) => onToggle(category.category, channel, next)}
      />
    </span>
  );

  return blocked === null ? control : <Tooltip content={blocked}>{control}</Tooltip>;
};

export const NotificationPreferencesPanel = () => {
  const personal = useNotificationPreferences();

  if (personal.settings.isError) {
    return (
      <ErrorState
        error={personal.settings.error}
        onRetry={() => void personal.settings.refetch()}
      />
    );
  }
  if (personal.settings.isPending) return <LoadingState rows={5} />;

  const { categories } = personal.settings.data;

  const columns: IColumn<TNotificationCategory>[] = [
    {
      key: 'category',
      header: 'When',
      width: 'grow',
      ui: true,
      render: (category) => (
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-primary">{category.label}</span>
          <span className="truncate text-meta text-muted" title={category.description}>
            {category.description}
          </span>
        </span>
      ),
    },
    ...CHANNELS.map((channel): IColumn<TNotificationCategory> => ({
      key: channel.key,
      header: channel.header,
      width: 'w-col-when',
      ui: true,
      render: (category) => (
        <Cell
          category={category}
          channel={channel.key}
          onToggle={personal.toggle}
          saving={personal.save.isPending}
        />
      ),
    })),
  ];

  return (
    <SectionPanel title="Just for you" note="only you see these, in every organization">
      {personal.save.isError && (
        <div className="px-card py-3">
          <MutationError error={personal.save.error} />
        </div>
      )}
      <Table
        tall
        layoutId="settings-notification-mine"
        columns={columns}
        rows={categories}
        rowKey={(category) => category.category}
      />
      <PanelFoot>
        You can turn off what the organization sends you, but not turn on what an owner turned off
        for everyone.
      </PanelFoot>
    </SectionPanel>
  );
};
